import { FormField, commentKeyForLabel } from "@/lib/database/forms";
import { isAnswerableField } from "@/features/forms/_logic/field-types";

/**
 * Helper function to check if a form field value is empty
 */
function isEmpty(value: any): boolean {
  if (value == null) return true; // null or undefined
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "string") return value.trim() === "";
  // Phone field object: check if phone digits are present
  if (typeof value === "object" && "phone" in value) {
    return !value.phone || value.phone.trim() === "";
  }
  return false; // numbers, booleans, objects are considered valid if they exist
}

/**
 * Resolves the effective min/max date bounds for a `date` field whose range
 * depends on other date fields (via `date_constraints`).
 *
 * The referenced fields are looked up by `id` within `formFields`, then their
 * current value is read from `formData` (keyed by label). Bounds are only
 * returned when the referenced field currently holds a non-empty date string,
 * so an unfilled dependency simply imposes no constraint. Native date inputs
 * treat `min`/`max` as inclusive, matching the "same day allowed" rule.
 *
 * @param field - The date field whose bounds are being computed
 * @param formFields - All fields in the form (used to resolve referenced ids)
 * @param formData - Current form values keyed by field label
 * @returns The resolved `min`/`max` ISO date strings (each optional)
 */
export function getDateFieldBounds(
  field: FormField,
  formFields: FormField[],
  formData: Record<string, unknown>
): { min?: string; max?: string } {
  if (field.type !== "date" || !field.date_constraints) return {};

  const valueOf = (fieldId?: string): string | undefined => {
    if (!fieldId) return undefined;
    const referenced = formFields.find((f) => f.id === fieldId);
    if (!referenced) return undefined;
    const value = formData[referenced.label];
    return typeof value === "string" && value.trim() !== "" ? value : undefined;
  };

  return {
    min: valueOf(field.date_constraints.min_field_id),
    max: valueOf(field.date_constraints.max_field_id),
  };
}

/**
 * Validates form submission data (used when users fill out forms)
 * Checks that all required fields have non-empty values and that any date
 * fields with cross-field `date_constraints` fall within their allowed range.
 */
export function validateFormSubmission(
  formFields: FormField[],
  formData: { [key: string]: any }
): {
  isValid: boolean;
  invalidFields: FormField[];
} {
  const invalidFields = formFields.filter((field) => {
    if (!isAnswerableField(field)) return false;
    if (field.is_required && isEmpty(formData[field.label])) return true;

    // Character limit: enforce on the answer and the optional comment.
    if (field.max_length) {
      const answer = formData[field.label];
      if (typeof answer === "string" && answer.length > field.max_length) return true;
      const comment = formData[commentKeyForLabel(field.label)];
      if (typeof comment === "string" && comment.length > field.max_length) return true;
    }

    // Cross-field date order check (e.g. departure on/after arrival).
    if (field.type === "date" && field.date_constraints) {
      const value = formData[field.label];
      if (typeof value === "string" && value.trim() !== "") {
        const { min, max } = getDateFieldBounds(field, formFields, formData);
        // ISO date strings (YYYY-MM-DD) compare correctly with string comparison.
        if (min && value < min) return true;
        if (max && value > max) return true;
      }
    }

    return false;
  });

  return {
    isValid: invalidFields.length === 0,
    invalidFields,
  };
}

/**
 * Initializes form data based on field types
 * Pre-fills email field if user email is provided
 * Sets ranking fields to their choice arrays
 */
export function initializeFormData(formFields: FormField[], userEmail?: string): { [key: string]: any } {
  return formFields.reduce(
    (acc, field) => {
      if (!isAnswerableField(field)) return acc;
      // Pre-fill email field using field.id for reliability (not dependent on label text)
      if (field.id === "email") {
        acc[field.label] = userEmail || "";
      } else if (field.type === "ranking") {
        acc[field.label] = field.choices || [];
      } else {
        acc[field.label] = "";
      }
      return acc;
    },
    {} as { [key: string]: any }
  );
}
